/**
 * API service for communicating with the dividend calendar backend
 */

import axios from 'axios';

// API URL configuration
// In production, set REACT_APP_API_URL to the Railway backend URL
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const API_BASE_URL = API_URL;

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

export interface DividendEvent {
  symbol: string;
  ex_date: string;
  dividend_amount: number;
  frequency?: string;
  source: string;
  payment_date?: string;
  updated_at?: string;
}

export interface TradeOrder {
  symbol: string;
  action: string;
  ex_date: string;
  dividend_amount: number;
  frequency?: string;
  entry_date?: string;
  exit_date?: string;
}

export interface TradingDay {
  date: string;
  buy_orders: TradeOrder[];
  sell_orders: TradeOrder[];
  total_trades: number;
}

export interface MonthSummary {
  year: number;
  month: number;
  trading_days: number;
  total_buy_orders: number;
  total_sell_orders: number;
  total_dividend_events: number;
  estimated_dividend_income: number;
  calendar?: TradingDay[];
}

export const dividendApi = {
  /**
   * Get all dividends
   */
  async getDividends(monthsAhead: number = 6, futureOnly: boolean = true): Promise<DividendEvent[]> {
    const response = await api.get('/api/dividends', {
      params: { months_ahead: monthsAhead, future_only: futureOnly },
    });
    return response.data;
  },

  /**
   * Get dividends for a specific month
   */
  async getDividendsForMonth(year: number, month: number): Promise<DividendEvent[]> {
    const response = await api.get(`/api/dividends/month/${year}/${month}`);
    return response.data;
  },

  /**
   * Get trades for a specific date
   */
  async getTradesForDate(date: string): Promise<TradingDay> {
    const response = await api.get(`/api/trades/date/${date}`);
    return response.data;
  },

  /**
   * Get monthly trading summary
   */
  async getMonthlyTrades(year: number, month: number): Promise<MonthSummary> {
    const response = await api.get(`/api/trades/month/${year}/${month}`);
    return response.data;
  },

  /**
   * Get trading calendar for date range
   */
  async getTradingCalendar(
    startDate?: string,
    endDate?: string,
    monthsAhead?: number
  ): Promise<TradingDay[]> {
    const response = await api.get('/api/calendar', {
      params: {
        start_date: startDate,
        end_date: endDate,
        months_ahead: monthsAhead,
      },
    });
    return response.data;
  },

  /**
   * Get list of tracked symbols
   */
  async getSymbols(): Promise<{ symbols: string[]; count: number }> {
    const response = await api.get('/api/symbols');
    return response.data;
  },

  /**
   * Get historical prices for symbols on specific dates
   */
  async getHistoricalPrices(items: { symbol: string; date: string }[]): Promise<Record<string, { symbol: string; date: string; price: number }>> {
    const response = await api.post('/api/prices/historical', { items });
    return response.data;
  },
};

// ============================================================================
// Authentication API
// ============================================================================

export interface User {
  id: number;
  username: string;
  email: string;
  full_name?: string;
  role: string;
  is_active: boolean;
  created_at: string;
  timezone: string;
}

export interface LoginRequest {
  username: string;
  password: string;
}

export interface RegisterRequest {
  username: string;
  email: string;
  password: string;
  full_name?: string;
}

export interface TokenResponse {
  access_token: string;
  token_type: string;
}

export const authApi = {
  async register(data: RegisterRequest): Promise<User> {
    const response = await api.post('/api/auth/register', data);
    return response.data;
  },

  async login(data: LoginRequest): Promise<TokenResponse> {
    const formData = new URLSearchParams();
    formData.append('username', data.username);
    formData.append('password', data.password);

    const response = await api.post('/api/auth/login', formData, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    });
    return response.data;
  },

  async getCurrentUser(token: string): Promise<User> {
    const response = await api.get('/api/auth/me', {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },
};

// ============================================================================
// Trading Accounts API
// ============================================================================

export interface TradingAccount {
  id: number;
  user_id: number;
  name: string;
  account_type: 'paper' | 'live';
  broker: string;
  account_number?: string;
  api_base_url?: string;
  initial_balance: number;
  current_balance: number;
  cash_balance: number;
  is_active: boolean;
  is_default: boolean;
  created_at: string;
  last_sync?: string;
}

export interface CreateAccountRequest {
  name: string;
  account_type: 'paper' | 'live';
  broker: string;
  api_key: string;
  api_secret: string;
  api_base_url?: string;
  is_default?: boolean;
  notes?: string;
}

export const accountsApi = {
  async listAccounts(token: string): Promise<TradingAccount[]> {
    const response = await api.get('/api/accounts', {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  async getAccount(accountId: number, token: string): Promise<TradingAccount> {
    const response = await api.get(`/api/accounts/${accountId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  async createAccount(data: CreateAccountRequest, token: string): Promise<TradingAccount> {
    const response = await api.post('/api/accounts', data, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  async deleteAccount(accountId: number, token: string): Promise<void> {
    await api.delete(`/api/accounts/${accountId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
  },
};

// ============================================================================
// Trading API
// ============================================================================

export interface Position {
  symbol: string;
  quantity: number;
  avg_entry_price: number;
  current_price: number;
  market_value: number;
  cost_basis: number;
  unrealized_pl: number;
  unrealized_plpc: number;
  side: string;
}

export interface Order {
  id: string;
  symbol: string;
  side: string;
  order_type: string;
  quantity: number;
  filled_qty: number;
  limit_price?: number;
  stop_price?: number;
  filled_avg_price?: number;
  status: string;
  time_in_force: string;
  created_at?: string;
  submitted_at?: string;
  filled_at?: string;
}

export interface AccountInfo {
  account_number: string;
  status: string;
  currency: string;
  cash: number;
  portfolio_value: number;
  buying_power: number;
  equity: number;
  last_equity: number;
  long_market_value: number;
  short_market_value: number;
  daytrade_count: number;
  pattern_day_trader: boolean;
}

export interface MarketOrderRequest {
  symbol: string;
  quantity: number;
  side: 'buy' | 'sell';
  time_in_force?: string;
  strategy_id?: number;
}

export const tradingApi = {
  async getAccountInfo(accountId: number, token: string): Promise<AccountInfo> {
    const response = await api.get(`/api/trading/${accountId}/info`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  async getPositions(accountId: number, token: string): Promise<Position[]> {
    const response = await api.get(`/api/trading/${accountId}/positions`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  async getOrders(accountId: number, token: string, statusFilter: string = 'open'): Promise<Order[]> {
    const response = await api.get(`/api/trading/${accountId}/orders`, {
      params: { status_filter: statusFilter },
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  async submitMarketOrder(accountId: number, orderData: MarketOrderRequest, token: string): Promise<Order> {
    const response = await api.post(`/api/trading/${accountId}/orders/market`, orderData, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  async cancelOrder(accountId: number, orderId: string, token: string): Promise<void> {
    await api.delete(`/api/trading/${accountId}/orders/${orderId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
  },

  async closePosition(accountId: number, positionId: number, token: string): Promise<Order> {
    const response = await api.post(`/api/trading/${accountId}/positions/${positionId}/close`, {}, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },
};

// ============================================================================
// Execution History API
// ============================================================================

export interface ExecutionOrder {
  id: number;
  symbol: string;
  side: string;
  order_type: string;
  quantity: number;
  filled_quantity: number;
  filled_price?: number;
  total_value?: number;
  status: string;
  broker_order_id?: string;
  submitted_at?: string;
  filled_at?: string;
  commission: number;
  fees: number;
}

export interface CompletedTrade {
  id: number;
  symbol: string;
  quantity: number;
  entry_date: string;
  entry_price: number;
  entry_cost: number;
  exit_date?: string;
  exit_price?: number;
  exit_proceeds?: number;
  ex_dividend_date?: string;
  dividend_per_share?: number;
  total_dividend?: number;
  dividend_captured: boolean;
  price_pnl?: number;
  dividend_income?: number;
  dividend_verified: boolean;
  total_pnl?: number;
  return_pct?: number;
  status: string;
  holding_days?: number;
  sizing_constraint?: string;
  total_fees: number;
}

export interface ExecutionSummary {
  account_id: number;
  account_name: string;
  total_trades: number;
  open_trades: number;
  closed_trades: number;
  winning_trades: number;
  losing_trades: number;
  total_pnl: number;
  total_price_pnl: number;
  total_dividend_income: number;
  total_dividend_verified: number;
  dividends_are_estimated: boolean;
  total_fees: number;
  all_time_pnl: number;
  ytd_pnl: number;
  one_year_pnl: number;
  win_rate: number;
  avg_return_pct: number;
  avg_holding_days: number;
  last_trade_date?: string;
}

export interface DailyExecution {
  date: string;
  buy_orders: number;
  sell_orders: number;
  total_orders: number;
  successful_orders: number;
  failed_orders: number;
  symbols_traded: string[];
  total_value: number;
}

export interface DividendTracking {
  position_id: number;
  symbol: string;
  quantity: number;
  ex_dividend_date?: string;
  expected_per_share: number;
  expected_total: number;
  actual_total?: number;
  payment_date?: string;
  status: 'paid' | 'pending' | 'overdue' | 'not_available';
  entry_date: string;
  exit_date?: string;
  position_status: string;
}

export interface ManualExecutionResult {
  status: string;
  account_id: number;
  account_name: string;
  trades_scheduled: number;
  orders_placed: number;
  successful_orders: number;
  failed_orders: number;
  executed_symbols: string[];
  error?: string;
}

export const executionsApi = {
  async getOrderHistory(
    accountId: number,
    token: string,
    days: number = 30,
    side?: string,
    symbol?: string
  ): Promise<ExecutionOrder[]> {
    const response = await api.get(`/api/executions/${accountId}/orders`, {
      params: { days, side, symbol },
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  async getTradeHistory(
    accountId: number,
    token: string,
    days: number = 90,
    statusFilter?: string,
    symbol?: string
  ): Promise<CompletedTrade[]> {
    const response = await api.get(`/api/executions/${accountId}/trades`, {
      params: { days, status_filter: statusFilter, symbol },
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  async getTradeDetail(accountId: number, tradeId: number, token: string): Promise<CompletedTrade> {
    const response = await api.get(`/api/executions/${accountId}/trades/${tradeId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  async getExecutionSummary(accountId: number, token: string, startDate?: string, endDate?: string): Promise<ExecutionSummary> {
    const params: Record<string, string> = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    const response = await api.get(`/api/executions/${accountId}/summary`, {
      params,
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  async getDailyExecutions(accountId: number, token: string, days: number = 30): Promise<DailyExecution[]> {
    const response = await api.get(`/api/executions/${accountId}/daily`, {
      params: { days },
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  async getDividendTracking(accountId: number, token: string, days: number = 365): Promise<DividendTracking[]> {
    const response = await api.get(`/api/executions/${accountId}/dividends`, {
      params: { days },
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  async triggerManualExecution(accountId: number, token: string): Promise<ManualExecutionResult> {
    const response = await api.post(`/api/executions/${accountId}/run`, {}, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },
};

// ============================================================================
// Strategies API
// ============================================================================

export const strategiesApi = {
  async listStrategies(token: string): Promise<Strategy[]> {
    const response = await api.get('/api/strategies', {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  async getStrategy(strategyId: number, token: string): Promise<Strategy> {
    const response = await api.get(`/api/strategies/${strategyId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  async createStrategy(data: StrategyCreate, token: string): Promise<Strategy> {
    const response = await api.post('/api/strategies', data, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  async updateStrategy(strategyId: number, data: StrategyUpdate, token: string): Promise<Strategy> {
    const response = await api.put(`/api/strategies/${strategyId}`, data, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  async toggleAutoTrading(strategyId: number, token: string): Promise<Strategy> {
    const response = await api.patch(`/api/strategies/${strategyId}/toggle-auto`, {}, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  async deleteStrategy(strategyId: number, token: string): Promise<void> {
    await api.delete(`/api/strategies/${strategyId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
  },
};

// ============================================================================
// Admin API (Admin users only)
// ============================================================================

export interface SystemHealth {
  scheduler_status: string;
  auto_trading_enabled: boolean;
  last_dividend_refresh?: string;
  last_strategy_execution?: string;
  total_users: number;
  total_accounts: number;
  active_accounts: number;
  open_positions: number;
  pending_orders: number;
  dividend_data_freshness?: string;
  total_dividend_events: number;
  upcoming_dividend_events: number;
}

export interface SchedulerRun {
  id: number;
  job_type: string;
  job_name?: string;
  started_at: string;
  completed_at?: string;
  duration_seconds?: number;
  status: string;
  result_summary?: Record<string, any>;
  error_message?: string;
}

export interface SystemLog {
  id: number;
  level: string;
  category: string;
  message: string;
  user_id?: number;
  account_id?: number;
  details?: Record<string, any>;
  error_type?: string;
  created_at: string;
}

export interface UpcomingTrade {
  symbol: string;
  trade_date: string;
  ex_date: string;
  dividend_amount: number;
  action: string;
  accounts: string[];
}

export interface UserSummary {
  id: number;
  username: string;
  email: string;
  role: string;
  is_active: boolean;
  accounts_count: number;
  total_trades: number;
  total_pnl: number;
  created_at: string;
  last_login?: string;
}

export interface ScheduledJob {
  name: string;
  schedule: string;
  enabled: boolean;
}

export interface SchedulerInfo {
  scheduler_enabled: boolean;
  auto_trading_enabled: boolean;
  jobs: ScheduledJob[];
}

export interface ErrorSummary {
  period_days: number;
  total_errors: number;
  by_category: Record<string, number>;
}

export interface AccountHealth {
  account_id: number;
  account_name: string;
  broker: string;
  account_type: string;
  is_active: boolean;
  connection_status: string;
  connection_error?: string;
  buying_power?: number;
  cash_balance?: number;
  portfolio_value?: number;
  last_trade_date?: string;
  open_positions: number;
  pending_orders: number;
  recent_errors: number;
  last_checked: string;
}

export interface ActivityFeedItem {
  id: string;
  event_type: string;
  timestamp: string;
  title: string;
  description: string;
  status?: string;
  account_name?: string;
  symbol?: string;
  details?: Record<string, any>;
}

export interface SystemSettingResponse {
  key: string;
  value: any;
  updated_at?: string;
}

// ============================================================================
// Strategy Types
// ============================================================================

export interface Strategy {
  id: number;
  account_id: number;
  name: string;
  strategy_type: string;
  description?: string;
  parameters: Record<string, any>;
  total_trades: number;
  winning_trades: number;
  losing_trades: number;
  total_pnl: number;
  total_fees: number;
  win_rate: number;
  is_active: boolean;
  is_automated: boolean;
  created_at: string;
  last_executed?: string;
}

export interface StrategyCreate {
  account_id: number;
  name: string;
  strategy_type: string;
  description?: string;
  parameters: Record<string, any>;
  is_automated?: boolean;
  notes?: string;
}

export interface StrategyUpdate {
  name?: string;
  description?: string;
  parameters?: Record<string, any>;
  is_active?: boolean;
  is_automated?: boolean;
  notes?: string;
}

export interface AdminStrategy extends Strategy {
  account_name: string;
  user_name: string;
}

export interface SkippedTrade {
  id: number;
  account_id?: number;
  account_name?: string;
  strategy_id?: number;
  scheduler_run_id?: number;
  symbol?: string;
  side?: string;
  ex_dividend_date?: string;
  dividend_amount?: number;
  reason: string;
  reason_detail?: string;
  market_price?: number;
  available_cash?: number;
  account_value?: number;
  calculated_shares?: number;
  created_at: string;
  trade_date?: string;
}

export interface SkippedTradesSummary {
  period_days: number;
  total_skipped: number;
  by_reason: Record<string, number>;
}

export const adminApi = {
  async getSystemHealth(token: string): Promise<SystemHealth> {
    const response = await api.get('/api/admin/health', {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  async getSchedulerRuns(
    token: string,
    jobType?: string,
    status?: string,
    days: number = 7
  ): Promise<SchedulerRun[]> {
    const response = await api.get('/api/admin/scheduler/runs', {
      params: { job_type: jobType, status, days },
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  async getSchedulerInfo(token: string): Promise<SchedulerInfo> {
    const response = await api.get('/api/admin/scheduler/next', {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  async getSystemLogs(
    token: string,
    level?: string,
    category?: string,
    days: number = 7
  ): Promise<SystemLog[]> {
    const response = await api.get('/api/admin/logs', {
      params: { level, category, days },
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  async getErrorSummary(token: string, days: number = 7): Promise<ErrorSummary> {
    const response = await api.get('/api/admin/logs/errors', {
      params: { days },
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  async getUpcomingTrades(token: string, days: number = 5): Promise<UpcomingTrade[]> {
    const response = await api.get('/api/admin/trades/upcoming', {
      params: { days },
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  async getUsers(token: string): Promise<UserSummary[]> {
    const response = await api.get('/api/admin/users', {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  async triggerJob(jobType: string, token: string): Promise<{ status: string; run_id: number; result: any }> {
    const response = await api.post(`/api/admin/scheduler/run/${jobType}`, {}, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  async updateUserRole(userId: number, role: string, token: string): Promise<{ message: string }> {
    const response = await api.post(`/api/admin/users/${userId}/role`, null, {
      params: { role },
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  async getAccountsHealth(token: string): Promise<AccountHealth[]> {
    const response = await api.get('/api/admin/accounts/health', {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  async getActivityFeed(token: string, days: number = 7, limit: number = 50): Promise<ActivityFeedItem[]> {
    const response = await api.get('/api/admin/activity', {
      params: { days, limit },
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  async getSettings(token: string): Promise<SystemSettingResponse[]> {
    const response = await api.get('/api/admin/settings', {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  async getSetting(key: string, token: string): Promise<SystemSettingResponse> {
    const response = await api.get(`/api/admin/settings/${key}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  async updateSetting(key: string, value: any, token: string): Promise<SystemSettingResponse> {
    const response = await api.put(`/api/admin/settings/${key}`, { value }, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  async toggleAccountActive(accountId: number, token: string): Promise<{ account_id: number; account_name: string; is_active: boolean }> {
    const response = await api.patch(`/api/admin/accounts/${accountId}/active`, {}, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  async getStrategies(token: string): Promise<AdminStrategy[]> {
    const response = await api.get('/api/admin/strategies', {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  async updateStrategy(strategyId: number, data: StrategyUpdate, token: string): Promise<AdminStrategy> {
    const response = await api.put(`/api/admin/strategies/${strategyId}`, data, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  async getSkippedTrades(
    token: string,
    days: number = 7,
    reason?: string,
    symbol?: string,
    accountId?: number,
    limit: number = 100
  ): Promise<SkippedTrade[]> {
    const response = await api.get('/api/admin/trades/skipped', {
      params: { days, reason, symbol, account_id: accountId, limit },
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  async getSkippedTradesSummary(token: string, days: number = 7): Promise<SkippedTradesSummary> {
    const response = await api.get('/api/admin/trades/skipped/summary', {
      params: { days },
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },
};

export default dividendApi;
